import "./styles/app.css";

import { renderHealth } from "./features/health-page/health-page";
import { renderHome } from "./features/home-page/home-page";
import { renderUiDemo } from "./features/ui-demo/ui-demo";
import { renderMyQuizzes } from "./features/my-quizzes/my-quizzes";
import { renderOrganizerSignIn } from "./features/organizer-login/organizer-login";
import { renderOrganizerQuizDetail } from "./features/organizer-quiz-detail/organizer-quiz-detail";
import { renderOrganizerQuizTranscriptPage } from "./features/organizer-quiz-transcript/organizer-quiz-transcript-page";
import { renderOrganizerWizard } from "./features/organizer-wizard/organizer-wizard";
import { renderParticipantQuiz } from "./features/participant-quiz/participant-quiz";
import type { SupportedLocale } from "./locales/catalog-registry";
import { createTranslator, resolveLocale } from "./lib/i18n";
import { readStoredLocale } from "./lib/locale-storage";
import { consumeParticipantInvitationToken } from "./lib/participant-invitation-token";
import { matchRoute, parseParticipantQuizId } from "./routes/match";

/**
 * SPA navigation aborts the previous route's {@link AbortController} before
 * starting the next {@link render} pass. Swallows that expected rejection so
 * devtools stay clean; other errors are surfaced via {@link console.error}.
 *
 * @param task - Async work for the route being entered.
 */
function attachRouteWork(task: Promise<void>): void {
  void task.catch((err: unknown) => {
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
    if (err instanceof Error && err.name === "AbortError") {
      return;
    }
    console.error(err);
  });
}

function isSpaPath(pathname: string): boolean {
  const normalized =
    pathname.endsWith("/") && pathname.length > 1
      ? pathname.slice(0, -1)
      : pathname;
  if (parseParticipantQuizId(normalized) !== undefined) {
    return true;
  }
  return (
    pathname === "/" ||
    pathname === "/health" ||
    pathname === "/health/" ||
    pathname === "/demo/ui" ||
    pathname === "/demo/ui/" ||
    pathname === "/organizer/sign-in" ||
    pathname === "/organizer/sign-in/" ||
    pathname === "/quizzes" ||
    pathname === "/quizzes/" ||
    pathname.startsWith("/quizzes/view/") ||
    pathname === "/quizzes/wizard" ||
    pathname.startsWith("/quizzes/wizard/")
  );
}

/**
 * Boots client routing and renders the active route into `#app` (no global chrome).
 *
 * @throws Error if `#app` is missing from the document.
 */
export function bootstrap(): void {
  const root = document.getElementById("app");
  if (!root) {
    throw new Error("missing #app");
  }

  let abortCtl: AbortController | null = null;

  const render = (): void => {
    abortCtl?.abort();
    abortCtl = null;

    const locale: SupportedLocale =
      typeof navigator !== "undefined"
        ? (readStoredLocale() ?? resolveLocale(navigator.language))
        : (readStoredLocale() ?? "en");
    document.documentElement.lang = locale;
    const t = createTranslator(locale);

    const path = window.location.pathname;
    const route = matchRoute(path);
    if (route.kind === "health") {
      abortCtl = new AbortController();
      renderHealth(root, t, { signal: abortCtl.signal, locale });
    } else if (route.kind === "ui-demo") {
      abortCtl = new AbortController();
      renderUiDemo(root, t, { locale, signal: abortCtl.signal });
    } else if (route.kind === "organizer-sign-in") {
      abortCtl = new AbortController();
      attachRouteWork(
        renderOrganizerSignIn(root, t, { locale, signal: abortCtl.signal }),
      );
    } else if (route.kind === "my-quizzes") {
      abortCtl = new AbortController();
      attachRouteWork(
        renderMyQuizzes(root, t, {
          signal: abortCtl.signal,
          locale,
        }),
      );
    } else if (route.kind === "organizer-wizard") {
      abortCtl = new AbortController();
      attachRouteWork(
        renderOrganizerWizard(root, t, {
          signal: abortCtl.signal,
          locale,
        }),
      );
    } else if (route.kind === "organizer-quiz-detail") {
      abortCtl = new AbortController();
      attachRouteWork(
        renderOrganizerQuizDetail(root, t, {
          signal: abortCtl.signal,
          locale,
          quizId: route.quizId,
        }),
      );
    } else if (route.kind === "organizer-quiz-transcript") {
      abortCtl = new AbortController();
      attachRouteWork(
        renderOrganizerQuizTranscriptPage(root, t, {
          signal: abortCtl.signal,
          locale,
          quizId: route.quizId,
          assignmentId: route.assignmentId,
        }),
      );
    } else if (route.kind === "participant-quiz") {
      abortCtl = new AbortController();
      const token = consumeParticipantInvitationToken(route.quizId);
      attachRouteWork(
        renderParticipantQuiz(root, t, {
          signal: abortCtl.signal,
          locale,
          quizId: route.quizId,
          invitationToken: token,
        }),
      );
    } else {
      abortCtl = new AbortController();
      attachRouteWork(
        renderHome(root, t, {
          locale,
          signal: abortCtl.signal,
        }),
      );
    }
  };

  root.addEventListener("click", (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const anchor = target.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }
    const url = new URL(anchor.href, window.location.origin);
    if (url.origin !== window.location.origin) {
      return;
    }
    if (!isSpaPath(url.pathname)) {
      return;
    }
    event.preventDefault();
    window.history.pushState(null, "", url.pathname + url.search + url.hash);
    render();
  });

  window.addEventListener("popstate", () => {
    render();
  });

  render();
}

bootstrap();
